import { createContext, createElement, useContext, useEffect, useMemo, type ReactNode } from "react";
import type { BookingLocale, BookingMessages } from "./types";
import { de } from "./de";
import { en } from "./en";

const messagesByLocale: Record<BookingLocale, BookingMessages> = {
  de,
  en,
};

export function getMessages(locale: BookingLocale): BookingMessages {
  return messagesByLocale[locale];
}

export function detectLocaleFromPath(pathname: string): BookingLocale {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (normalized === "/en" || normalized.startsWith("/en/")) {
    return "en";
  }
  return "de";
}

export function localePathPrefix(locale: BookingLocale): string {
  return locale === "en" ? "/en" : "";
}

export function buildLocalePath(locale: BookingLocale, path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const prefix = localePathPrefix(locale);
  if (!prefix) return normalized;
  if (normalized === "/") return prefix;
  return `${prefix}${normalized}`;
}

export type LocaleContextValue = {
  locale: BookingLocale;
  messages: BookingMessages;
  pathPrefix: string;
  localePath: (path: string) => string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  locale,
  messages,
  children,
}: {
  locale: BookingLocale;
  messages?: BookingMessages;
  children: ReactNode;
}) {
  const resolvedMessages = messages ?? getMessages(locale);
  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      messages: resolvedMessages,
      pathPrefix: localePathPrefix(locale),
      localePath: (path: string) => buildLocalePath(locale, path),
    }),
    [locale, resolvedMessages],
  );

  useEffect(() => {
    document.documentElement.lang = locale;
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute("content", value.messages.meta.description);
    }
  }, [locale, value.messages.meta.description]);

  return createElement(LocaleContext.Provider, { value }, children);
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return context;
}

export type { BookingLocale, BookingMessages } from "./types";
